import React, { useMemo } from 'react';

import {
  Box,
  HStack,
  Skeleton,
  Stack,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { MapPin } from 'lucide-react';

import {
  StatusTag,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
} from '@/components/core';
import { EngineerIcon, JobIcon } from '@/constant';
import { SearchOutcomeHit, RelatedWorks } from '@/types/search';
import {
  aggregateCommonResults,
  aggregateCommonTypes,
  formatJobCount,
  getRelatedContextForHit,
} from '@/utils/searchOutcomes';

import { RelatedBreakdownChart } from './RelatedBreakdownChart';
import { RelatedKpiRow } from './RelatedKpiRow';
import { RelatedRecentJobs } from './RelatedRecentJobs';

interface RelatedWorksPanelProps {
  hit: SearchOutcomeHit;
  relatedWorks?: RelatedWorks;
  isLoading?: boolean;
  isError?: boolean;
  outcomeHits?: SearchOutcomeHit[];
  onOpenJob?: (jobId: number) => void;
}

export const RelatedWorksPanel: React.FC<RelatedWorksPanelProps> = ({
  hit,
  relatedWorks,
  isLoading = false,
  isError = false,
  outcomeHits,
  onOpenJob,
}) => {
  const context = useMemo(
    () => getRelatedContextForHit(relatedWorks, hit),
    [relatedWorks, hit]
  );

  const availableTabs = useMemo(() => {
    const tabs: Array<'site' | 'engineer' | 'type'> = [];
    if (context.site) tabs.push('site');
    if (context.engineer) tabs.push('engineer');
    if (context.type) tabs.push('type');
    return tabs;
  }, [context]);

  const typeResults = useMemo(
    () => aggregateCommonResults(context.type?.commonResults),
    [context.type?.commonResults]
  );

  const siteTypes = useMemo(
    () => aggregateCommonTypes(context.site?.commonTypes),
    [context.site?.commonTypes]
  );

  if (isLoading && !relatedWorks) {
    return (
      <Stack
        gap={3}
        pt={3}
        mt={3}
        borderTop='1px solid'
        borderColor='border.light'
        data-cy='related-works-skeleton'
        onClick={event => event.stopPropagation()}
      >
        <Text
          fontSize='xs'
          fontWeight='semibold'
          color='tabs.font-selected'
          textTransform='uppercase'
        >
          Related context
        </Text>
        <HStack spacing={3} minW={0} w='full'>
          <Skeleton height='64px' flex={1} minW={0} rounded='xl' />
          <Skeleton height='64px' flex={1} minW={0} rounded='xl' />
          <Skeleton height='64px' flex={1} minW={0} rounded='xl' />
        </HStack>
        <Skeleton height='160px' rounded='xl' />
        <Skeleton height='72px' rounded='xl' />
      </Stack>
    );
  }

  if (isError && !relatedWorks) {
    return (
      <Box
        pt={3}
        mt={3}
        borderTop='1px solid'
        borderColor='border.light'
        onClick={event => event.stopPropagation()}
      >
        <Text fontSize='xs' opacity={0.55} color='fonts.primary'>
          Related context unavailable right now.
        </Text>
      </Box>
    );
  }

  if (availableTabs.length === 0) {
    return (
      <Box
        pt={3}
        mt={3}
        borderTop='1px solid'
        borderColor='border.light'
        onClick={event => event.stopPropagation()}
      >
        <Text fontSize='xs' opacity={0.55} color='fonts.primary'>
          No related site, engineer, or type context for this job.
        </Text>
      </Box>
    );
  }

  return (
    <Stack
      gap={3}
      pt={3}
      mt={3}
      minW={0}
      w='full'
      borderTop='1px solid'
      borderColor='border.light'
      data-cy='related-works-panel'
      onClick={event => event.stopPropagation()}
    >
      <HStack justify='space-between' align='center'>
        <Text
          fontSize='xs'
          fontWeight='semibold'
          color='tabs.font-selected'
          textTransform='uppercase'
          letterSpacing='0.02em'
        >
          Related context
        </Text>
        {isLoading ? (
          <Text fontSize='xs' opacity={0.45} color='fonts.primary'>
            Refreshing…
          </Text>
        ) : null}
      </HStack>

      <Tabs variant='line' size='sm' isLazy>
        <TabList>
          {availableTabs.includes('site') ? (
            <Tab icon={MapPin}>Site</Tab>
          ) : null}
          {availableTabs.includes('engineer') ? (
            <Tab icon={EngineerIcon}>Engineer</Tab>
          ) : null}
          {availableTabs.includes('type') ? (
            <Tab icon={JobIcon}>Type</Tab>
          ) : null}
        </TabList>

        <TabPanels>
          {availableTabs.includes('site') && context.site ? (
            <TabPanel>
              <Stack gap={4} align='stretch' minW={0} w='full'>
                <Text
                  fontSize='sm'
                  fontWeight='medium'
                  color='fonts.primary'
                  noOfLines={{ base: 3, xl: 2 }}
                >
                  {context.site.address}
                </Text>
                <RelatedKpiRow
                  totalJobs={context.site.totalJobs}
                  completionRate={context.site.completionRate}
                  avgEndDeltaMins={context.site.avgEndDeltaMins}
                  uniqueEngineers={context.site.uniqueEngineers}
                />
                {context.site.repeatFailures?.length ? (
                  <Box>
                    <Text
                      fontSize='xs'
                      fontWeight='semibold'
                      color='tags.gold.font'
                      textTransform='uppercase'
                      letterSpacing='0.02em'
                      mb={2}
                    >
                      Repeat failures
                    </Text>
                    <Wrap spacing={1.5} shouldWrapChildren>
                      {context.site.repeatFailures.map(failure => (
                        <WrapItem
                          key={`${failure.type}-${failure.failures}`}
                          maxW='100%'
                        >
                          <StatusTag
                            status='warning'
                            size='sm'
                            isReadOnly
                            maxW='100%'
                            whiteSpace='normal'
                            h='auto'
                            py={1}
                          >
                            {failure.type} × {failure.failures}
                          </StatusTag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                ) : null}
                <RelatedBreakdownChart
                  title='Common types'
                  data={siteTypes}
                  emptyLabel='No common types for this site'
                />
                <RelatedRecentJobs
                  jobs={context.site.recentJobs}
                  hits={outcomeHits}
                  onOpenJob={onOpenJob}
                />
              </Stack>
            </TabPanel>
          ) : null}

          {availableTabs.includes('engineer') && context.engineer ? (
            <TabPanel>
              <Stack gap={4} align='stretch' minW={0} w='full'>
                <Text
                  fontSize='sm'
                  fontWeight='medium'
                  color='fonts.primary'
                  noOfLines={{ base: 2, xl: 1 }}
                >
                  {context.engineer.engineerName}
                </Text>
                <RelatedKpiRow
                  totalJobs={context.engineer.totalJobs}
                  completionRate={context.engineer.completionRate}
                  avgEndDeltaMins={context.engineer.avgEndDeltaMins}
                />
                <Wrap spacing={1.5}>
                  {typeof context.engineer.typeExperience?.jobsOfThisType ===
                  'number' ? (
                    <WrapItem>
                      <Tag size='sm' isReadOnly isSelected>
                        {formatJobCount(
                          context.engineer.typeExperience.jobsOfThisType
                        )}{' '}
                        of this type
                      </Tag>
                    </WrapItem>
                  ) : null}
                  {typeof context.engineer.contactExperience
                    ?.jobsForThisContact === 'number' ? (
                    <WrapItem>
                      <Tag size='sm' isReadOnly isSelected>
                        {formatJobCount(
                          context.engineer.contactExperience.jobsForThisContact
                        )}{' '}
                        for this contact
                      </Tag>
                    </WrapItem>
                  ) : null}
                </Wrap>
                <RelatedRecentJobs
                  jobs={context.engineer.recentJobs}
                  hits={outcomeHits}
                  onOpenJob={onOpenJob}
                />
              </Stack>
            </TabPanel>
          ) : null}

          {availableTabs.includes('type') && context.type ? (
            <TabPanel>
              <Stack gap={4} align='stretch' minW={0} w='full'>
                <Text
                  fontSize='sm'
                  fontWeight='medium'
                  color='fonts.primary'
                  noOfLines={{ base: 3, xl: 2 }}
                >
                  {context.type.typeName}
                </Text>
                <RelatedKpiRow
                  totalJobs={context.type.totalJobs}
                  completionRate={context.type.completionRate}
                  avgEndDeltaMins={context.type.avgEndDeltaMins}
                />
                <RelatedBreakdownChart
                  title='Common results'
                  data={typeResults}
                />
                <RelatedRecentJobs
                  jobs={context.type.recentJobs}
                  hits={outcomeHits}
                  onOpenJob={onOpenJob}
                />
              </Stack>
            </TabPanel>
          ) : null}
        </TabPanels>
      </Tabs>
    </Stack>
  );
};
